/**
 * IuFinderPage — "which ad unit does this page use?" (docs/iu-finder-spec.md WP4).
 *
 * Fires GET /api/iu-index (saved HARs, JWT) and GET /proxy/iu-observations
 * (live buffer; api/proxyAuth.js attaches the token) in parallel, then
 * utils/iuFinderMerge.js lines the answers up side by side. Either source may
 * fail without blanking the other. No LLM anywhere in this feature.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { API_BASE } from '../apiBase';
import { authHeader, handleAuthResponse } from '../api/authFetch';
import { PROXY_API_BASE } from '../api/proxyAuth';
import { mergeAnswers, toCsv, harLabel, pickTargeting } from '../utils/iuFinderMerge';
import { api } from '../api/client';

const PROXY_BASE = PROXY_API_BASE || 'http://localhost:8001';
const LS_KEY = 'iuFinder.prefs';
const DAY = 24 * 3600 * 1000;

const DEFAULT_PREFS = { mode: 'page', windowKind: '7d', latestN: 20, includeLive: true, platform: '', thirdParty: false };

function loadPrefs() {
  try {
    const p = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    return p && typeof p === 'object' ? { ...DEFAULT_PREFS, ...p } : DEFAULT_PREFS;
  } catch { return DEFAULT_PREFS; }
}
function savePrefs(p) { try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch { /* per-viewer convenience only */ } }

export const PLACEHOLDERS = { page: 'world cup, outkick betting, politics…', iu: '/4145/fnc/iosmob/art/pol' };

async function getJson(url, init, onResponse) {
  const res = await fetch(url, init);
  if (onResponse) onResponse(res);
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.error || j?.message || ''; } catch { /* ignore */ }
    throw new Error(`${res.status}${d ? ` — ${d}` : ''}`);
  }
  return res.json();
}

const settle = (p) => p.then((v) => v, (error) => ({ error }));

const card = { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: 14 };
const input = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 13 };
const btn = { ...input, cursor: 'pointer' };
const mono = { fontFamily: 'var(--font-mono)', fontSize: 12 };
const th = { textAlign: 'left', padding: '4px 8px', color: 'var(--muted)', fontWeight: 500, fontSize: 12, borderBottom: '1px solid var(--line)' };
const td = { padding: '4px 8px', fontSize: 12, color: 'var(--ink)', verticalAlign: 'top' };

function CopyIu({ text }) {
  const [done, setDone] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1200); } catch { /* clipboard blocked */ }
  };
  return <button type="button" onClick={click} style={{ ...btn, padding: '0 6px', marginLeft: 6, fontSize: 11 }}>{done ? 'Copied' : 'Copy'}</button>;
}

const pair = (s) => (s ? `${s.requests} / ${s.pages}` : '—');
const when = (ts) => { if (ts == null) return '—'; const d = new Date(ts); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(); };
const joined = (a) => (a && a.length ? a.join(', ') : '—');

function exampleTag(o, source, harsById) {
  if (o && o.harId) return `${harLabel(harsById && harsById.get(o.harId), o.harId)} #${o.entryIndex}`;
  return source === 'live' || (o && o.liveKey) ? 'Live' : '';
}

function Banner({ children }) {
  return <div role="alert" style={{ background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>{children}</div>;
}

/* Targeting for one IU: every cust_params key it was sent, with values and
   counts. Keys that vary across requests are highlighted — that is usually
   what ad-ops is looking for. Replaces the old per-page "Examples" list, which
   repeated the same page + IU once per HAR entry. */
function TargetingPanel({ i, harsById, onOpen }) {
  const picked = pickTargeting(i);
  return (
    <div style={{ display: 'grid', gap: 10, padding: '6px 4px 10px 18px' }}>
      {!picked ? (
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          No targeting stored for these requests yet. Saved HARs indexed before targeting was added are
          re-read in the background; search again in a few minutes.
        </span>
      ) : (
        <div>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>
            cust_params on {picked.t.rows} {picked.source === 'live' ? 'live' : 'saved-HAR'} request{picked.t.rows === 1 ? '' : 's'}
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {picked.t.keys.map((k) => (
                <tr key={k.key}>
                  <td style={{ ...td, ...mono, width: 160, color: k.varies ? 'var(--warn-text)' : 'var(--ink)', fontWeight: k.varies ? 600 : 400 }}>{k.key}</td>
                  <td style={td}>
                    {k.values.map((v) => (
                      <span key={v.value} style={{ ...mono, marginRight: 12, wordBreak: 'break-all' }}>
                        {v.value === '' ? '(empty)' : v.value}
                        {(k.varies || k.present < picked.t.rows) && <span style={{ color: 'var(--muted)' }}> ×{v.count}</span>}
                      </span>
                    ))}
                    {k.more > 0 && <span style={{ color: 'var(--muted)' }}>+{k.more} more values</span>}
                  </td>
                  <td style={{ ...td, color: 'var(--muted)', whiteSpace: 'nowrap', width: 120 }}>
                    {k.varies ? 'differs' : ''}{k.present < picked.t.rows ? `${k.varies ? ' · ' : ''}sent on ${k.present} of ${picked.t.rows}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {i.samples && i.samples.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>Latest requests</span>
          {i.samples.map((s, k) => (
            <div key={k} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{when(s.ts)}</span>
              <span style={{ color: 'var(--ink)', wordBreak: 'break-all' }}>{s.contentUrl || '(no page URL)'}</span>
              <Chip size="small" variant="outlined" label={exampleTag(s.origin, s.source, harsById)} />
              <button type="button" onClick={() => onOpen(i, s)} style={{ ...btn, padding: '0 8px', fontSize: 11 }}>Open</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* One GAM request as indexed: page, IU, sizes and every cust_params pair.
   Used for saved-HAR samples (opening the HAR itself is owner-only) and for
   live rows that carry no dashboard id. */
function RequestDialog({ open, onClose, iu, sample, harsById }) {
  if (!sample) return null;
  const pairs = Object.entries(sample.cust || {});
  const text = [`IU: ${iu}`, `Page: ${sample.contentUrl || '(none)'}`, `Sizes: ${joined(sample.sizes)}`,
    ...pairs.map(([k, v]) => `${k}=${v}`)].join('\n');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 15 }}>
        <span style={mono}>{iu}</span>
        <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 8 }}>{exampleTag(sample.origin, sample.source, harsById)} · {when(sample.ts)}</span>
      </DialogTitle>
      <DialogContent>
        <div style={{ fontSize: 12, color: 'var(--ink)', display: 'grid', gap: 4, marginBottom: 12 }}>
          <div><span style={{ color: 'var(--muted)' }}>Page </span><span style={{ wordBreak: 'break-all' }}>{sample.contentUrl || '(no page URL)'}</span></div>
          <div><span style={{ color: 'var(--muted)' }}>Sizes </span>{joined(sample.sizes)}{sample.platform ? ` · ${sample.platform}` : ''}</div>
          {sample.partial && <div style={{ color: 'var(--warn-text)' }}>USB capture — the URL was cut at ~4KB, so some params may be missing.</div>}
        </div>
        {pairs.length ? (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th}>cust_params key</th><th style={th}>Value</th></tr></thead>
            <tbody>
              {pairs.map(([k, v]) => (
                <tr key={k}><td style={{ ...td, ...mono }}>{k}</td><td style={{ ...td, ...mono, wordBreak: 'break-all' }}>{v === '' ? '(empty)' : v}</td></tr>
              ))}
            </tbody>
          </table>
        ) : (
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            {sample.cust ? 'This request sent no cust_params.' : 'Targeting was not stored for this request yet (indexed before targeting was added).'}
          </span>
        )}
        <div style={{ marginTop: 12 }}><CopyIu text={text} /></div>
      </DialogContent>
    </Dialog>
  );
}

function ResultCard({ r, harsById }) {
  const navigate = useNavigate();
  const [openIu, setOpenIu] = useState(null);
  const [dialog, setDialog] = useState(null); // { iu, sample }
  const toggle = (iu) => setOpenIu((cur) => (cur === iu ? null : iu));
  // A live row with a dashboard id opens the full request there (raw URL, every
  // param, validator findings); anything else opens the indexed view.
  const onOpen = (i, s) => {
    const id = s.origin && s.origin.requestId;
    if (s.source === 'live' && id) {
      navigate(`/?req=${encodeURIComponent(id)}`, { state: { from: '/checks?mode=iu', fromLabel: 'IU Finder' } });
    } else {
      setDialog({ iu: i.iu, sample: s });
    }
  };
  return (
    <div style={{ ...card, display: 'grid', gap: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span style={{ ...mono, fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{r.template}</span>
        {r.matchedOn.map((m) => <Chip key={m} size="small" variant="outlined" label={`matched: ${m}`} />)}
        {r.isNew && (
          <Tooltip title={`First captured ${when(r.firstSeen)} — no saved capture before this window has this page type. Check it isn't serving on a default or fallback IU.`}>
            <Chip size="small" label="New" sx={{ background: 'var(--info-bg)', color: 'var(--ink)' }} />
          </Tooltip>
        )}
        {r.drift && (
          <Tooltip title="More than one ad-unit path for this page type on one platform — likely a mapping bug">
            <Chip size="small" label="Drift" sx={{ background: 'var(--warn-bg)', color: 'var(--ink)' }} />
          </Tooltip>
        )}
      </Box>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr>
            <th style={th}>IU</th><th style={th}>HAR req / pages</th><th style={th}>Live req / pages</th>
            <th style={th}>Platforms</th><th style={th}>App versions</th><th style={th}>Sizes</th><th style={th}>Last seen</th>
          </tr></thead>
          <tbody>
            {r.ius.map((i) => {
              const isOpen = openIu === i.iu;
              return (
                <React.Fragment key={i.iu}>
                  <tr onClick={() => toggle(i.iu)} style={{ cursor: 'pointer', background: isOpen ? 'var(--row-active)' : undefined }}
                      title="Show targeting (cust_params) for this ad unit">
                    <td style={td}>
                      <span style={{ display: 'inline-block', width: 12, color: 'var(--muted)' }}>{isOpen ? '▾' : '▸'}</span>
                      <span style={mono}>{i.iu || '(unreadable)'}</span>
                      {i.iu && <span onClick={(e) => e.stopPropagation()}><CopyIu text={i.iu} /></span>}
                    </td>
                    <td style={td}>{pair(i.bySource.hars)}</td>
                    <td style={td}>{pair(i.bySource.live)}</td>
                    <td style={td}>{joined(i.platforms)}</td>
                    <td style={td}>{joined(i.appVersions)}</td>
                    <td style={td}>{joined(i.sizes)}</td>
                    <td style={td}>{when(i.lastSeen)}</td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={7} style={{ ...td, background: 'var(--surface)' }}>
                      <TargetingPanel i={i} harsById={harsById} onOpen={onOpen} />
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <RequestDialog open={!!dialog} onClose={() => setDialog(null)} iu={dialog && dialog.iu}
        sample={dialog && dialog.sample} harsById={harsById} />
    </div>
  );
}

export default function IuFinderPage({ embedded = false } = {}) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [merged, setMerged] = useState(null);
  const [searched, setSearched] = useState('');
  // "Pick saved HARs" — the user's own HAR list (the ones they can open); the
  // window options still search every user's HARs.
  const [harList, setHarList] = useState(null);
  const [harListError, setHarListError] = useState('');
  const [picked, setPicked] = useState([]);

  const setPref = (patch) => setPrefs((p) => { const n = { ...p, ...patch }; savePrefs(n); return n; });
  const harsOn = prefs.windowKind !== 'off';
  const picking = prefs.windowKind === 'pick';

  useEffect(() => {
    if (!picking || harList) return;
    let alive = true;
    Promise.resolve()
      .then(() => api.list({ limit: 100, sort: 'createdAt' }))
      .then((rows) => { if (alive) setHarList(Array.isArray(rows) ? rows : (rows && rows.items) || []); })
      .catch((e) => { if (alive) setHarListError(String((e && e.message) || e)); });
    return () => { alive = false; };
  }, [picking, harList]);
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // ?q= / ?iu= (+ ?platform=) — links from the Ask analyst's iu_lookup land on results.
  useEffect(() => {
    let sp;
    try { sp = new URLSearchParams(window.location.search); } catch { return; }
    const iu = (sp.get('iu') || '').trim();
    const q = (sp.get('q') || '').trim();
    const har = (sp.get('har') || '').trim();
    if (har) {
      // ?har=<id> (Captures → Find IUs): that capture pre-picked, every IU in it.
      setPref({ windowKind: 'pick', mode: 'page' });
      setPicked([har]);
      setText(q);
      search({ term: q, mode: 'page', platform: prefs.platform, harIds: [har] });
      return;
    }
    if (!iu && !q) return;
    const platform = ['ios', 'android', 'web'].includes(sp.get('platform')) ? sp.get('platform') : prefs.platform;
    const mode = iu ? 'iu' : 'page';
    setPref({ mode, platform });
    setText(iu || q);
    search({ term: iu || q, mode, platform });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search(override) {
    const term = ((override && override.term != null ? override.term : text) || '').trim();
    const pickIds = (override && override.harIds) || (picking ? picked : null);
    // An explicit pick may search with no term: "every IU in these captures".
    if (!term && !(pickIds && pickIds.length)) return;
    const mode = (override && override.mode) || prefs.mode;
    const platform = override ? override.platform : prefs.platform;
    const key = mode === 'iu' ? 'iu' : 'q';
    const params = new URLSearchParams();
    if (term) params.set(key, term);
    if (platform) params.set('platform', platform);
    if (prefs.thirdParty) params.set('includeThirdParty', '1');
    const liveParams = new URLSearchParams(params);
    const harParams = new URLSearchParams(params);
    if (pickIds) {
      if (!pickIds.length) return; // the Search button is disabled until something is picked
      harParams.set('harIds', pickIds.join(','));
    } else if (prefs.windowKind === 'latest') harParams.set('latestHars', String(Math.max(1, Number(prefs.latestN) || 20)));
    else if (prefs.windowKind === '24h') harParams.set('since', String(Date.now() - DAY));
    else if (prefs.windowKind === '7d') harParams.set('since', String(Date.now() - 7 * DAY));
    else if (prefs.windowKind === '30d') harParams.set('since', String(Date.now() - 30 * DAY));
    setBusy(true);
    const [hars, live] = await Promise.all([
      (pickIds || harsOn) ? settle(getJson(`${API_BASE}/api/iu-index?${harParams}`, { headers: { Accept: 'application/json', ...authHeader() } }, handleAuthResponse)) : Promise.resolve(null),
      // An explicit pick asks about THOSE captures — live rows would muddy the answer.
      (prefs.includeLive && !pickIds) ? settle(getJson(`${PROXY_BASE}/proxy/iu-observations?${liveParams}`)) : Promise.resolve(null),
    ]);
    setMerged(mergeAnswers({ hars, live }));
    setSearched(term);
    setBusy(false);
  }

  function exportCsv() {
    const blob = new Blob([toCsv(merged)], { type: 'text/csv' });
    const a = document.createElement('a');
    const safe = searched.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 40) || 'results';
    a.href = URL.createObjectURL(blob);
    a.download = `iu-finder-${safe}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const cov = merged && merged.coverage;
  const harsById = useMemo(() => new Map(((cov && cov.harList) || []).map((m) => [m.id, m])), [cov]);
  return (
    <div style={{ padding: embedded ? 0 : 20, display: 'grid', gap: 12, maxWidth: 1200, margin: embedded ? 0 : '0 auto' }}>
      <div>
        {!embedded && <h2 style={{ margin: 0, color: 'var(--ink)' }}>IU Finder</h2>}
        <p style={{ margin: embedded ? 0 : '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
          Which ad unit does a page use? Searches saved HARs and the live capture — no phone needed for saved HARs.
        </p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); search(); }} style={{ ...card, display: 'grid', gap: 10 }}>
        <div role="group" aria-label="Search mode" style={{ display: 'flex', gap: 6 }}>
          {[['page', 'Page → IU'], ['iu', 'IU → Pages']].map(([m, label]) => (
            <button key={m} type="button" aria-pressed={prefs.mode === m} onClick={() => setPref({ mode: m })}
              style={{ ...btn, background: prefs.mode === m ? 'var(--info-bg)' : 'var(--surface)', fontWeight: prefs.mode === m ? 600 : 400 }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input aria-label="Search" value={text} onChange={(e) => setText(e.target.value)} placeholder={PLACEHOLDERS[prefs.mode]}
            style={{ ...input, flex: 1, ...(prefs.mode === 'iu' ? mono : {}) }} />
          <button type="submit" disabled={busy || (picking ? !picked.length : !text.trim())} style={btn}>{busy ? 'Searching…' : 'Search'}</button>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 13, color: 'var(--ink)' }}>
          <label>Saved HARs{' '}
            <select aria-label="Saved HARs window" value={prefs.windowKind} onChange={(e) => setPref({ windowKind: e.target.value })} style={input}>
              <option value="24h">Last 24h</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option>
              <option value="latest">Latest N HARs</option><option value="pick">Pick saved HARs…</option><option value="off">Off</option>
            </select>
          </label>
          {prefs.windowKind === 'latest' && (
            <input aria-label="Latest N HARs" type="number" min={1} value={prefs.latestN} onChange={(e) => setPref({ latestN: e.target.value })} style={{ ...input, width: 70 }} />
          )}
          <label><input type="checkbox" checked={prefs.includeLive} onChange={(e) => setPref({ includeLive: e.target.checked })} /> Include live capture</label>
          <label>Platform{' '}
            <select aria-label="Platform" value={prefs.platform} onChange={(e) => setPref({ platform: e.target.value })} style={input}>
              <option value="">All</option><option value="ios">iOS</option><option value="android">Android</option><option value="web">Web</option>
            </select>
          </label>
          <label><input type="checkbox" checked={prefs.thirdParty} onChange={(e) => setPref({ thirdParty: e.target.checked })} /> Include third-party creatives</label>
        </div>
        {picking && (
          <div aria-label="Saved HAR picker" style={{ border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', maxHeight: 220, overflowY: 'auto', padding: 6, display: 'grid', gap: 2 }}>
            {harListError && <span style={{ color: 'var(--muted)', fontSize: 12 }}>Could not load your saved HARs: {harListError}</span>}
            {!harListError && !harList && <span style={{ color: 'var(--muted)', fontSize: 12 }}>Loading your saved HARs…</span>}
            {harList && !harList.length && <span style={{ color: 'var(--muted)', fontSize: 12 }}>You have no saved HARs yet — save a capture from the Dashboard first.</span>}
            {harList && harList.map((h) => (
              <label key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, color: 'var(--ink)', cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(h.id)} onChange={() => togglePick(h.id)} />
                <span style={{ fontWeight: 500 }}>{h.title || h.pageUrl || h.host || h.id}</span>
                <span style={{ color: 'var(--muted)' }}>{when(h.createdAt)}</span>
              </label>
            ))}
            {harList && harList.length > 0 && (
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>{picked.length} picked · your own saved HARs (window options search everyone's)</span>
            )}
          </div>
        )}
      </form>

      {cov && cov.harsError && <Banner>Saved HARs unavailable: {cov.harsError}</Banner>}
      {cov && cov.liveError && <Banner>Live capture unavailable: {cov.liveError}</Banner>}

      {merged && (
        <>
          <div style={{ color: 'var(--muted)', fontSize: 13, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              Searched {cov.hars == null ? 'no' : cov.hars} saved HAR{cov.hars === 1 ? '' : 's'}
              {cov.harList && cov.harList.length > 0 && (
                <> ({cov.harList.slice(0, 3).map((m) => harLabel(m, m.id)).join(', ')}
                {cov.hars > 3 ? `, and ${cov.hars - 3} more` : ''})</>
              )}
              {' '}+ {cov.liveRows == null ? 'no' : cov.liveRows} live rows
              {cov.gamRows != null ? ` (${cov.gamRows} GAM requests)` : ''}
            </span>
            {merged.partialRows > 0 && <span>{merged.partialRows} partial (USB URL cut)</span>}
            {merged.unreadableRows > 0 && <span>{merged.unreadableRows} unreadable</span>}
            {merged.results.length > 0 && <button type="button" onClick={exportCsv} style={btn}>Export CSV</button>}
          </div>

          {merged.partialRows > 0 && (
            <Banner>USB captures are request-only: IU and targeting, no fill/CPM; some URLs were cut at ~4KB, so those rows may lack an IU or page.</Banner>
          )}

          {merged.results.length === 0 ? (
            <div style={card}>
              <strong style={{ color: 'var(--ink)' }}>No match in this window.</strong>
              <p style={{ margin: '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
                This is not proof the page has no IU — it only means nothing captured here matched. Widen the window, or capture the page and search again.
              </p>
            </div>
          ) : merged.results.map((r) => <ResultCard key={r.template} r={r} harsById={harsById} />)}
        </>
      )}
    </div>
  );
}
